import { TextInput, useGetList } from "react-admin";
import { FieldValues, FormProvider, Path, useForm, useFormContext, useWatch } from "react-hook-form";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { Student } from "../../providers/studentsProvider";
import { Instructor } from "../../providers/instructorsProvider";
import { grants } from "../../backoffice.access_control";
import { SelectInput } from "../../inputs/SelectInput";
import { InstructorInput } from "../../inputs/InstructorInput";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { StudentInputField } from "./StudentInputField";
import { DrivingLicenseClassInput } from "../../inputs/DrivingLicenseClassInput";
import { VehiclesInput } from "../../inputs/VehiclesInput";
import { OWN_MOTORCYCLE_PSEUDO_ID } from "../../model/VehicleType";
import {
  DrivingLicenseClass,
  isForBus,
  isForBusWithTrailer,
  isForCar,
  isForCarWithTrailer,
  isForMotorcycle,
  isForTractor,
  isForTruck,
  isForTruckWithTrailer,
} from "../../model/DrivingLicenseClass";
import { BookedTraining } from "../../model/BookedTraining";
import { Money } from "../../model/Money";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { PriceSuggestion } from "./PriceSuggestion";
import { TheoryExamTrainingSelector } from "./TheoryExamTrainingSelector";
import { TheoryExamPriceSelector } from "./TheoryExamPriceSelector";
import { Form2Theme } from "../../misc/Form2";
import { LocalizedError } from "../../utils/LocalizedError";
import { handleBookingError } from "../../utils/handleBookingError";
import { assertNever } from "../../backoffice.utils";
import { StartingPointInput } from "../../inputs/StartingPointInput";
import { startingPointsProvider } from "../../providers/startingPointsProvider";
import { BookCalendarEventWithoutInviteButton } from "../../buttons/BookCalendarEventWithoutInviteButton";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { useInsufficientCompulsoryLessonsWarning } from "../../hooks/useInsufficientCompulsoryLessonsWarning";
import { getPracticalExamEligibilityDate, getTheoryExamEligibilityDate } from "../../model/examEligibility";
import { formatDate } from "../../utils/calendar";
import { autovioColors } from "../../misc/backofficeTheme";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";
import { PracticalExam, TheoryExam } from "../../model/autovioCalendarEvents";
import { v4 as uuidv4 } from "uuid";
import { CreateCalendarEventDto, serverAPI } from "../../api/server.api";
import { VehicleType } from "../../model/VehicleType";
import { StartingPoint } from "../../providers/startingPointsProvider";
import _pick from "lodash/pick";

interface CommonCreateExamFormValues {
  dateTime: DateTime | null;
  durationInMinutes: number;
  drivingLessonType?: never;
  examType: "theoretischePruefung" | "praktischePruefung" | "";
  student: Student | null;
  noteForStudent: string | null;
}

interface CreateTheoryExamFormValues extends CommonCreateExamFormValues {
  theoryExamLocationId: string;
  // bookedTrainings selected when there are multiple available trainings.
  bookedTrainingIds: string[];
  // Exam price selected when the available trainings have differing theory-exam prices.
  theoryExamPriceSelection: { bookedTrainingId: string; price: Money } | null;
}

interface CreatePracticalExamFormValues extends CommonCreateExamFormValues {
  durationInMinutes: number;
  instructorId: string;
  practicalExamStartingPointId: string;
  carId: string;
  motorcycleId: string;
  truckId: string;
  busId: string;
  tractorId: string;
  trailerId: string;
  drivingLicenseClass: DrivingLicenseClass | "";
}

export interface CreateExamFormValues extends CreateTheoryExamFormValues, CreatePracticalExamFormValues {}

export interface BookTheoryExamParams {
  examType: "theoretischePruefung";
  dateTime: DateTime;
  instructorId?: never;
  studentId: string;
  bookedTrainingId?: string;
  bookedTrainingIds: string[];
  startingPointId: string;
  manualPrice?: Money;
  noteForStudent?: string;
}

export interface BookPracticalExamParams {
  examType: "praktischePruefung";
  instructorId: string;
  studentId: string;
  bookedTrainingId: string;
  dateTime: DateTime;
  duration: { minutes: number };
  startingPointId: string;
  vehicleIds: Array<string>;
  noteForStudent?: string;
}

export const PRACTICAL_EXAM_DURATION_OPTIONS = [15, 40, 55, 60, 70, 80, 85];

function _getDefaultNoteForStudent(examType: CreateExamFormValues["examType"]): string {
  let result = "Bitte denke daran, deinen gültigen Personalausweis, Aufenthaltstitel oder Reisepass mitzubringen.";
  if (examType === "praktischePruefung") {
    result += "\nFalls du eine Sehhilfe benötigst, bringe diese bitte ebenfalls mit.";
  }
  return result;
}

export function CreateExamForm(props: {
  onSuccess: () => void;
  instructor?: Instructor;
  student?: Student;
  vehicles?: Array<{ id: string; type: VehicleType }>;
  drivingLicenseClass?: DrivingLicenseClass;
  startingPoint?: StartingPoint;
  initialExamType?: "theoretischePruefung" | "praktischePruefung";
  initialDateTime?: DateTime;
  initialDurationInMinutes?: number;
  isHistoricalLesson?: boolean;
}) {
  const {
    instructor,
    student: initialStudent,
    vehicles,
    drivingLicenseClass: initialDrivingLicenseClass,
    startingPoint,
    initialDateTime,
    initialExamType,
    initialDurationInMinutes,
    isHistoricalLesson = false,
    onSuccess,
  } = props;
  const [insufficientPrepaidCredit, setInsufficientPrepaidCredit] = useState<boolean>(false);
  const [vehicle1, vehicle2] = vehicles ?? [];
  const defaultValues = {
    examType: initialExamType || "",
    dateTime: initialDateTime || null,
    durationInMinutes: initialDurationInMinutes ?? 0,
    instructorId: instructor?.id || "",
    student: initialStudent ?? null,
    theoryExamLocationId: startingPoint && initialExamType === "theoretischePruefung" ? startingPoint.id : "",
    bookedTrainingIds: [],
    theoryExamPriceSelection: null,
    practicalExamStartingPointId: startingPoint && initialExamType === "praktischePruefung" ? startingPoint.id : "",
    carId: vehicle1?.type === "car" ? vehicle1.id : vehicle2?.type === "car" ? vehicle2.id : "",
    motorcycleId: vehicle1?.type === "motorcycle" ? vehicle1.id : "",
    truckId: vehicle1?.type === "truck" ? vehicle1.id : "",
    busId: vehicle1?.type === "bus" ? vehicle1.id : "",
    tractorId: vehicle1?.type === "tractor" ? vehicle1.id : "",
    trailerId: vehicle1?.type === "trailer" ? vehicle1.id : vehicle2?.type === "trailer" ? vehicle2.id : "",
    drivingLicenseClass: initialDrivingLicenseClass ?? "",
    noteForStudent: _getDefaultNoteForStudent(initialExamType ?? ""),
  } satisfies CreateExamFormValues;

  const formProps = useForm<CreateExamFormValues>({ defaultValues });
  const { examType, student, dateTime, drivingLicenseClass, bookedTrainingIds } = formProps.watch();
  const noteForStudentIsDirty = formProps.formState.dirtyFields.noteForStudent;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { data: studentExams = [] } = useGetList<TheoryExam | PracticalExam>(
    "calendarEvents",
    { filter: { studentId: student?.id, type: ["TheoryExam", "PracticalExam"], drivingSchoolId } },
    { enabled: !!student },
  );
  const examDateWarning = getExamDateWarning({ examType, student, dateTime, drivingLicenseClass, studentExams });
  const theoryExamEligibleTrainings = useMemo(
    () => (student ? getTheoryExamBookedTrainings(student, { isHistoricalLesson }) : []),
    [student, isHistoricalLesson],
  );

  useEffect(() => {
    if (examType === "theoretischePruefung" && theoryExamEligibleTrainings.length === 1) {
      formProps.setValue("bookedTrainingIds", [theoryExamEligibleTrainings[0].id]);
      return;
    } else {
      formProps.setValue("bookedTrainingIds", []);
    }
    formProps.setValue("theoryExamPriceSelection", null);
  }, [theoryExamEligibleTrainings, examType]);

  useEffect(() => {
    if (!noteForStudentIsDirty) {
      formProps.setValue("noteForStudent", _getDefaultNoteForStudent(examType), { shouldDirty: false });
    }
  }, [examType, noteForStudentIsDirty]);

  function validate(formValues: CreateExamFormValues): BookTheoryExamParams | BookPracticalExamParams {
    const {
      examType,
      dateTime,
      durationInMinutes,
      student,
      theoryExamLocationId,
      bookedTrainingIds,
      theoryExamPriceSelection,
      practicalExamStartingPointId,
      carId,
      motorcycleId,
      truckId,
      busId,
      tractorId,
      trailerId,
      drivingLicenseClass,
      noteForStudent,
    } = formValues;
    if (!dateTime || !DateTime.isDateTime(dateTime) || !dateTime.isValid) {
      throw new LocalizedError("Bitte wähle ein gültiges Datum und eine Uhrzeit aus.");
    }
    if (!examType) {
      throw new Error(`Invalid examType: ${examType}`);
    }
    if (!student) {
      throw new Error(`Invalid student: ${student}`);
    }
    if (examType === "theoretischePruefung") {
      if (!theoryExamLocationId) {
        throw new Error(`Invalid theoryExamLocationId: ${theoryExamLocationId}`);
      }
      if (theoryExamEligibleTrainings.length === 0) {
        throw new LocalizedError(
          `${student.firstName} verfügt über keine aktive Ausbildung, die eine theoretische Prüfung beinhaltet.`,
        );
      }
      if (bookedTrainingIds.length === 0) {
        throw new LocalizedError("Bitte wähle mindestens ein Paket aus.");
      }
      const selectedTrainings = theoryExamEligibleTrainings.filter((it) => bookedTrainingIds.includes(it.id));
      // Prefer booked training for motorcycle if present ...
      let trainingFields: { bookedTrainingIds: string[]; bookedTrainingId?: string; manualPrice?: Money };
      if (bookedTrainingIds.length === 1) {
        trainingFields = {
          bookedTrainingIds,
          // Used by PriceSuggestion.
          bookedTrainingId: bookedTrainingIds[0],
        };
      } else {
        const primaryBookedTraining =
          selectedTrainings.find((it) => isForMotorcycle(it.drivingLicenseClass)) ?? selectedTrainings[0];
        // Reorder so the primary (motorcycle-first) training is first — the server derives the legacy scalar mirror from `bookedTrainingIds[0]`.
        const orderedIds = [
          primaryBookedTraining.id,
          ...selectedTrainings.filter((it) => it.id !== primaryBookedTraining.id).map((it) => it.id),
        ];
        trainingFields = {
          bookedTrainingIds: orderedIds,
          ...(theoryExamPriceSelection ? { manualPrice: theoryExamPriceSelection.price } : {}),
        };
      }
      if (isHistoricalLesson) {
        trainingFields.bookedTrainingId ??= trainingFields.bookedTrainingIds[0];
        if (!trainingFields.bookedTrainingId) {
          throw new Error(`Invalid bookedTrainingId: ${trainingFields.bookedTrainingId}`);
        }
      }
      return {
        examType: "theoretischePruefung",
        studentId: student.id,
        ...trainingFields,
        dateTime,
        startingPointId: theoryExamLocationId,
        ...(noteForStudent ? { noteForStudent } : {}),
      } satisfies BookTheoryExamParams;
    } else if (examType === "praktischePruefung") {
      if (!durationInMinutes) {
        throw new Error(`Invalid durationInMinutes: ${durationInMinutes}`);
      }
      const instructorId = formValues.instructorId;
      if (!instructorId) {
        throw new Error(`Invalid instructorId: ${instructorId}`);
      }
      if (!drivingLicenseClass) {
        throw new Error(`Invalid drivingLicenseClass: ${drivingLicenseClass}`);
      }
      let vehicleIds: Array<string>;
      if (isForCar(drivingLicenseClass)) {
        if (!carId) {
          throw new Error(`Invalid carId: ${carId}`);
        }
        if (isForCarWithTrailer(drivingLicenseClass)) {
          if (!trailerId) {
            throw new Error(`Invalid trailerId: ${trailerId}`);
          }
          vehicleIds = [carId, trailerId];
        } else {
          vehicleIds = [carId];
        }
      } else if (isForTruck(drivingLicenseClass)) {
        if (!truckId) {
          throw new Error(`Invalid truckId: ${truckId}`);
        }
        if (isForTruckWithTrailer(drivingLicenseClass)) {
          if (!trailerId) {
            throw new Error(`Invalid trailerId: ${trailerId}`);
          }
          vehicleIds = [truckId, trailerId];
        } else {
          vehicleIds = [truckId];
        }
      } else if (isForBus(drivingLicenseClass)) {
        if (!busId) {
          throw new Error(`Invalid busId: ${busId}`);
        }
        if (isForBusWithTrailer(drivingLicenseClass)) {
          if (!trailerId) {
            throw new Error(`Invalid trailerId: ${trailerId}`);
          }
          vehicleIds = [busId, trailerId];
        } else {
          vehicleIds = [busId];
        }
      } else if (isForMotorcycle(drivingLicenseClass)) {
        if (!motorcycleId) {
          throw new Error(`Invalid motorcycleId: ${motorcycleId}`);
        }
        const studentUsesOwnMotorcycle = motorcycleId === OWN_MOTORCYCLE_PSEUDO_ID;
        if (!carId) {
          throw new Error(`Invalid carId: ${carId}`);
        }
        vehicleIds = studentUsesOwnMotorcycle ? [carId] : [motorcycleId, carId];
      } else if (isForTractor(drivingLicenseClass)) {
        if (!tractorId) {
          throw new Error(`Invalid tractorId: ${tractorId}`);
        }
        vehicleIds = [tractorId];
      } else {
        throw new Error(`Unsupported driving license class: ${drivingLicenseClass}`);
      }
      const bookedTraining = student.bookedTrainings.find(
        (it) =>
          it.drivingLicenseClass === drivingLicenseClass &&
          !it.isFinished &&
          ((dateTime.isValid && dateTime.diffNow("milliseconds").milliseconds < 0) || !it.isCancelled),
      );
      if (!bookedTraining) {
        throw new LocalizedError(
          `${student.firstName} has kein Paket für die Führerscheinklasse ${drivingLicenseClass} gebucht.`,
        );
      }
      if (!practicalExamStartingPointId) {
        throw new Error(`Invalid practicalExamStartingPointId: ${practicalExamStartingPointId}`);
      }
      return {
        examType: "praktischePruefung",
        instructorId,
        studentId: student.id,
        bookedTrainingId: bookedTraining.id,
        dateTime,
        duration: { minutes: durationInMinutes },
        startingPointId: practicalExamStartingPointId,
        vehicleIds,
        ...(noteForStudent ? { noteForStudent } : {}),
      } satisfies BookPracticalExamParams;
    } else {
      assertNever(examType);
    }
  }

  const onSubmit =
    ({ studentRsvp }: { studentRsvp: "pending" | "accepted" }) =>
    async (formValues: CreateExamFormValues) => {
      try {
        const params = validate(formValues);
        const isInThePast = params.dateTime < DateTime.now();
        const { examType, startingPointId, ...rest } = params;
        const { data: examStartingPoint } = await startingPointsProvider.getOne("startingPoints", {
          id: startingPointId,
        });
        const location = examStartingPoint.postalAddress;
        if (isHistoricalLesson) {
          const durationMinutes =
            examType === "theoretischePruefung" ? 45 : "duration" in rest ? rest.duration.minutes : 45;
          const vehicleIds = "vehicleIds" in rest ? rest.vehicleIds : [];
          const usesOwnMotorcycle = formValues.motorcycleId === OWN_MOTORCYCLE_PSEUDO_ID;
          const bookedTrainingId =
            rest.bookedTrainingId ?? ("bookedTrainingIds" in rest ? rest.bookedTrainingIds?.[0] : "") ?? "";
          const start = toIsoString(params.dateTime);
          const end = toIsoString(params.dateTime.plus({ minutes: durationMinutes }));
          const payload = {
            id: uuidv4(),
            type: "DrivingLesson" as const,
            drivingLessonType: examType,
            ...(rest.instructorId ? { instructorId: rest.instructorId } : {}),
            start,
            end,
            student: {
              bookedTrainingId,
              id: rest.studentId,
              rsvp: "accepted" as const,
            },
            startLocation: location,
            ...(examType === "praktischePruefung" && {
              vehicleId: usesOwnMotorcycle ? OWN_MOTORCYCLE_PSEUDO_ID : (vehicleIds[0] ?? null),
              vehicle2Id: usesOwnMotorcycle ? formValues.carId || null : (vehicleIds[1] ?? null),
            }),
            ...(rest.noteForStudent ? { noteForStudent: rest.noteForStudent } : {}),
            manualStudentGross: { amount: 0, currency: "EUR" as const },
            endedAt: toIsoString(DateTime.now()),
          } satisfies CreateCalendarEventDto;
          await serverAPI.createCalendarEvent(payload);
        } else if (params.examType === "theoretischePruefung") {
          await serverAPI.createCalendarEvent({
            id: uuidv4(),
            type: "TheoryExam",
            start: toIsoString(params.dateTime),
            student: {
              id: params.studentId,
              bookedTrainingIds: params.bookedTrainingIds,
              rsvp: studentRsvp,
            },
            location: _pick(location, ["street", "postalCode", "city"]),
            ...(params.manualPrice ? { manualStudentGross: params.manualPrice } : {}),
            ...(params.noteForStudent ? { noteForStudent: params.noteForStudent } : {}),
          });
        } else {
          await serverAPI.createCalendarEvent({
            id: uuidv4(),
            type: "DrivingLesson",
            drivingLessonType: "praktischePruefung",
            instructorId: params.instructorId,
            start: toIsoString(params.dateTime),
            end: toIsoString(params.dateTime.plus(params.duration)),
            student: {
              id: params.studentId,
              bookedTrainingId: params.bookedTrainingId,
              rsvp: studentRsvp,
            },
            startLocation: _pick(location, ["street", "postalCode", "city"]),
            resourceIds: params.vehicleIds,
            ...(params.noteForStudent ? { noteForStudent: params.noteForStudent } : {}),
          });
        }
        showSuccessToast(
          isHistoricalLesson || isInThePast ? "Prüfung erfolgreich nachgetragen." : "Einladung erfolgreich versandt.",
        );
        onSuccess();
      } catch (error: any) {
        if (
          error.details?.code === "student.not-enough-credit" ||
          error?.response?.data?.type?.endsWith?.("student.not-enough-credit")
        ) {
          setInsufficientPrepaidCredit(true);
        } else if (!(await handleBookingError(error, student))) {
          const errorMessage =
            isHistoricalLesson || (formValues.dateTime && formValues.dateTime < DateTime.now())
              ? "Fehler beim Nachtragen der Prüfung"
              : "Fehler beim Versenden der Einladung";
          handleError(errorMessage, error);
        }
      }
    };
  const isSubmitButtonsDisabled = insufficientPrepaidCredit && !!dateTime && dateTime > DateTime.now();
  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <form onSubmit={formProps.handleSubmit(onSubmit({ studentRsvp: "pending" }))} style={{ height: "100%" }}>
          <Column height="100%" minHeight={500} gap="1em">
            <DateTimeInput
              source="dateTime"
              allowPastDates={isHistoricalLesson || grants.has("Exam:historical:create")}
              allowFutureDates={!isHistoricalLesson}
              size="small"
              warning={examDateWarning}
            />
            {!(isHistoricalLesson && initialExamType) && <_ExamTypeInputField />}
            {examType === "theoretischePruefung" && (
              <_TheoryExamFormInputs
                instructor={instructor}
                isHistoricalLesson={isHistoricalLesson}
                activeBookedTrainings={theoryExamEligibleTrainings}
              />
            )}
            {examType === "praktischePruefung" && (
              <_PracticalExamFormInputs instructor={instructor} isHistoricalLesson={isHistoricalLesson} />
            )}
            {!isHistoricalLesson &&
              (examType === "praktischePruefung" ||
                (examType === "theoretischePruefung" && bookedTrainingIds.length === 1)) && (
                <PriceSuggestion validate={validate} />
              )}
            {!isHistoricalLesson && insufficientPrepaidCredit && student && (
              <Row gap="4px" alignItems="center">
                <InfoOutlineIcon sx={{ fill: autovioColors.red, fontSize: "18px" }} />
                <Typography variant="body2" color="error">
                  {student.name} hat nicht genug Guthaben.
                </Typography>
              </Row>
            )}
            <Row sx={{ justifyContent: "flex-end", mt: "auto", gap: 1 }}>
              {!isHistoricalLesson && grants.has("Exam:bookWithoutInvite") && (
                <BookCalendarEventWithoutInviteButton
                  eventType="exam"
                  disabled={isSubmitButtonsDisabled}
                  onSubmit={formProps.handleSubmit(onSubmit({ studentRsvp: "accepted" }))}
                />
              )}
              <SaveButton2
                label={
                  isHistoricalLesson || (dateTime && dateTime < DateTime.now())
                    ? "Termin nachtragen"
                    : "Einladung senden"
                }
                disabled={isSubmitButtonsDisabled}
              />
            </Row>
          </Column>
        </form>
      </FormProvider>
    </Form2Theme>
  );
}

function _ExamTypeInputField() {
  const { control } = useFormContext<CreatePracticalExamFormValues>();
  const insufficientCompulsoryLessonsWarning = useInsufficientCompulsoryLessonsWarning({
    examType: useWatch({ control, name: "examType" }),
    student: useWatch({ control, name: "student" }),
    drivingLicenseClass: useWatch({ control, name: "drivingLicenseClass" }),
    dateTime: useWatch({ control, name: "dateTime" }),
  });
  return (
    <SelectInput
      source="examType"
      label="Art der Prüfung"
      options={[
        ["theoretischePruefung", "Theorieprüfung"],
        ["praktischePruefung", "Praktische Prüfung"],
      ]}
      validate={(value) => (value ? true : "Bitte wähle die Art der Prüfung aus.")}
      size="small"
      warning={insufficientCompulsoryLessonsWarning}
    />
  );
}

function _TheoryExamFormInputs({
  instructor,
  isHistoricalLesson,
  activeBookedTrainings,
}: {
  instructor?: Instructor;
  isHistoricalLesson: boolean;
  activeBookedTrainings: BookedTraining[];
}) {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { watch } = useFormContext<CreateTheoryExamFormValues>();
  const student = watch("student");
  const bookedTrainingIds = watch("bookedTrainingIds");
  return (
    <>
      {!isHistoricalLesson && (
        <StudentInputField {...(instructor ? { instructorId: instructor.id } : { drivingSchoolId })} size="small" />
      )}
      {student && (
        <>
          <StartingPointInput
            source="theoryExamLocationId"
            drivingLessonType="theoretischePruefung"
            student={student}
            size="small"
          />
          {activeBookedTrainings.length > 1 && (
            <TheoryExamTrainingSelector activeBookedTrainings={activeBookedTrainings} />
          )}
          <TextInput
            source="noteForStudent"
            label="Hinweis für Fahrschüler"
            multiline
            rows={2}
            helperText={false}
            sx={{ "&.ra-input": { mb: 0 } }}
          />
          {!isHistoricalLesson && bookedTrainingIds.length > 1 && (
            <TheoryExamPriceSelector activeBookedTrainings={activeBookedTrainings} />
          )}
        </>
      )}
    </>
  );
}

function _PracticalExamFormInputs({
  instructor,
  isHistoricalLesson,
}: {
  instructor?: Instructor;
  isHistoricalLesson: boolean;
}) {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { watch } = useFormContext<CreatePracticalExamFormValues>();
  const student = watch("student");
  const drivingLicenseClass = watch("drivingLicenseClass");
  const instructorId = watch("instructorId");
  const dateTime = watch("dateTime");
  const excludeCancelledBookedTrainings = !dateTime?.isValid || dateTime.diffNow("milliseconds").milliseconds >= 0;

  return (
    <>
      {(isHistoricalLesson || !instructor) && <InstructorInput source="instructorId" size="small" />}
      {!isHistoricalLesson && instructorId && (
        <StudentInputField {...(instructor ? { instructorId: instructor.id } : { drivingSchoolId })} size="small" />
      )}
      {student && (
        <DrivingLicenseClassInput
          source="drivingLicenseClass"
          label="Führerscheinklasse"
          student={student}
          size="small"
          excludeCancelledBookedTrainings={excludeCancelledBookedTrainings}
        />
      )}
      {student && <_PracticalExamDurationInput source="durationInMinutes" size="small" />}
      {student && drivingLicenseClass && (
        <VehiclesInput
          instructorId={instructorId}
          drivingLessonType="praktischePruefung"
          drivingLicenseClass={drivingLicenseClass}
          size="small"
          required
        />
      )}
      {student && (
        <StartingPointInput
          source="practicalExamStartingPointId"
          drivingLessonType="praktischePruefung"
          instructorId={instructorId}
          student={student}
          size="small"
        />
      )}
      {student && <TextInput source="noteForStudent" label="Hinweis für Fahrschüler" multiline rows={2} />}
    </>
  );
}

const _PracticalExamDurationInput = (props: { source: Path<FieldValues>; size?: "small" | "medium" }) => {
  const options = PRACTICAL_EXAM_DURATION_OPTIONS.map((it) => [it, `${it} Minuten`] as [number, string]);
  const { watch, setValue } = useFormContext<CreateExamFormValues>();
  const student = watch("student");
  const drivingLicenseClass = watch("drivingLicenseClass");
  useEffect(() => {
    if (student && drivingLicenseClass) {
      const bookedTraining = student.bookedTrainings.find((it) => it.drivingLicenseClass === drivingLicenseClass);
      if (isForMotorcycle(drivingLicenseClass)) {
        if (drivingLicenseClass === "AM") {
          setValue("durationInMinutes", 55);
        } else if (bookedTraining?.drivingLicenseClassAdvancement) {
          setValue("durationInMinutes", 60);
        } else {
          setValue("durationInMinutes", 70);
        }
      } else if (isForCar(drivingLicenseClass)) {
        setValue("durationInMinutes", 55);
      }
    }
  }, [student, drivingLicenseClass]);
  return <SelectInput label="Dauer" source={props.source} options={options} size={props.size} />;
};

/**
 * Returns a warning if the selected exam date is before the earliest date at which the student
 * is allowed to take the exam (based on their age and driving license class), otherwise `null`.
 */
function getExamDateWarning({
  examType,
  student,
  dateTime,
  drivingLicenseClass,
  studentExams,
}: Pick<CreateExamFormValues, "examType" | "student" | "dateTime" | "drivingLicenseClass"> & {
  studentExams: (TheoryExam | PracticalExam)[];
}): string | null {
  if (!student || !dateTime?.isValid || !drivingLicenseClass) {
    return null;
  }
  const hasPreviousExam = studentExams.some((exam) =>
    examType === "praktischePruefung"
      ? "drivingLessonType" in exam && exam.drivingLessonType === "praktischePruefung"
      : "type" in exam && exam.type === "TheoryExam",
  );
  const eligibilityDate =
    examType === "theoretischePruefung"
      ? getTheoryExamEligibilityDate(student, drivingLicenseClass)
      : examType === "praktischePruefung"
        ? getPracticalExamEligibilityDate(student, drivingLicenseClass)
        : null;
  if (eligibilityDate && dateTime.startOf("day") < eligibilityDate) {
    return `Warnung: ${hasPreviousExam ? "Nächste " : ""}Prüfung für Fahrschüler frühestens ablegbar ab: ${formatDate(eligibilityDate)}.`;
  }
  return null;
}

function toIsoString(dateTime: DateTime): string {
  if (!DateTime.isDateTime(dateTime) || !dateTime.isValid) {
    throw new LocalizedError("Bitte wähle ein gültiges Datum und eine Uhrzeit aus.");
  }
  const iso = dateTime.toISO();
  if (!iso) {
    throw new LocalizedError("Bitte wähle ein gültiges Datum und eine Uhrzeit aus.");
  }
  return iso;
}

export function getTheoryExamBookedTrainings(
  student: Student,
  { isHistoricalLesson }: { isHistoricalLesson: boolean },
): BookedTraining[] {
  return student.bookedTrainings.filter(
    (it) => it.includesTheoryExam && !it.isCancelled && (isHistoricalLesson || !it.hasPassedTheoryExam),
  );
}
